import React, { useState, useRef, useEffect } from "react";
import { IonButton, IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftImage: string;
    rightImage: string;
}

const HomeItems: Record<string, string[]> = {
    "Black": ["#000"],
    "Blue": ["#0000FF"],
    "Red": ["#FF0000"],
    "Green": ["#008000"],
    "Yellow": ["#FFFF00"],
    "Orange": ["#FFA500"],
    "Pink": ["#FFC0CB"],
    "Purple": ["#800080"],
    "Brown": ["#A52A2A"],
    "Grey": ["#808080"],
};


const MatchColor: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Get element position relative to .match-container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect(); setHeight((container.scrollHeight) + 'px')
        const containerRect = container.getBoundingClientRect();

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left image click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right image click and draw SVG line
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget);

            // Check if the connection is correct
            const isCorrect = questions.some(pair => selectedLeft.id === rightId);

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Fetch questions dynamically
    const fetchQuestions = () => {
        const usedKeys: string[] = [];
        const homeKeys = Object.keys(HomeItems);
        const shuffledKeys = shuffleArray([...homeKeys]);
        const selectedKeys = shuffledKeys.slice(0, 4); console.log(selectedKeys)
        const pairs = selectedKeys.map((key, index) => {
            const images = HomeItems[key];
            const randomImage = images[Math.floor(Math.random() * images.length)];
            usedKeys.push(randomImage);
        });
        const newQuestions: ImagePair[] = [];

        const suffledNumbers = shuffleArray([...usedKeys]); //console.log(suffledNumbers)
        selectedKeys.map((key, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: usedKeys.indexOf(suffledNumbers[index]) + 1,
                leftImage: key,
                rightImage: suffledNumbers[index],
            });
        })

        //console.log(newQuestions)

        setQuestions(newQuestions);
    };

    useEffect(() => {
        resetConnections();
        fetchQuestions();
        document.title = "Match the Color – UKG Color Recognition Activities | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        resetConnections();
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 2000);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Engage your UKG child with fun 'Match the Color' activities. Help kids identify and match colors to real-life objects through interactive worksheets and games." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/colors/match-color" />
            </Helmet>
            {!showImage ? (
                <>
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Match the Color</h1>

                            <p>
                                Colors are one of the first things children learn and observe in their surroundings. In this engaging <strong>“Match the Color”</strong> activity, UKG students will learn to identify colors and match them to corresponding real-world objects like apples, bananas, or balloons. This builds visual memory, vocabulary, and observation skills.
                            </p>

                            <h2>🎨 What Will Children Learn?</h2>
                            <ul>
                                <li>Identify primary and secondary colors</li>
                                <li>Match colors to familiar objects</li>
                                <li>Strengthen visual perception and color vocabulary</li>
                                <li>Understand how colors are part of everyday life</li>
                            </ul>

                            <h2>🌈 Colors Covered</h2>
                            <ul>
                                <li>Red – apple, tomato</li>
                                <li>Blue – sky, jeans</li>
                                <li>Green – leaf, frog</li>
                                <li>Yellow – sun, banana</li>
                                <li>Orange – orange fruit, pumpkin</li>
                                <li>Purple – grapes, plum</li>
                                <li>Pink – flower, eraser</li>
                                <li>Black and White – zebra, panda</li>
                            </ul>



                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/smallest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Smallest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/biggest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Biggest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/number-comparison"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Number Comparison</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🖍️ At-Home Color Practice Ideas</h2>
                            <ul>
                                <li>Ask your child to find objects of a specific color at home</li>
                                <li>Play color sorting games using blocks or clothes</li>
                                <li>Use finger painting or crayons to explore color blending</li>
                                <li>Read books that highlight colorful illustrations</li>
                            </ul>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Match the Color</h1>

                        <p>
                            Colors are one of the first things children learn and observe in their surroundings. In this engaging <strong>“Match the Color”</strong> activity, UKG students will learn to identify colors and match them to corresponding real-world objects like apples, bananas, or balloons. This builds visual memory, vocabulary, and observation skills.
                        </p>

                        <h2>🎨 What Will Children Learn?</h2>
                        <ul>
                            <li>Identify primary and secondary colors</li>
                            <li>Match colors to familiar objects</li>
                            <li>Strengthen visual perception and color vocabulary</li>
                            <li>Understand how colors are part of everyday life</li>
                        </ul>

                        <h2>🌈 Colors Covered</h2>
                        <ul>
                            <li>Red – apple, tomato</li>
                            <li>Blue – sky, jeans</li>
                            <li>Green – leaf, frog</li>
                            <li>Yellow – sun, banana</li>
                            <li>Orange – orange fruit, pumpkin</li>
                            <li>Purple – grapes, plum</li>
                            <li>Pink – flower, eraser</li>
                            <li>Black and White – zebra, panda</li>
                        </ul>



                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/mathematics/smallest-number"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Smallest Number</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/biggest-number"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Biggest Number</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/mathematics/number-comparison"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Number Comparison</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🖍️ At-Home Color Practice Ideas</h2>
                        <ul>
                            <li>Ask your child to find objects of a specific color at home</li>
                            <li>Play color sorting games using blocks or clothes</li>
                            <li>Use finger painting or crayons to explore color blending</li>
                            <li>Read books that highlight colorful illustrations</li>
                        </ul>
                    </article>
                    <div ref={containerRef} className="contentPart additionWord matchImage match-container" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>{/* SVG for drawing lines */}
                        <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                            {connections.map((conn, index) => (
                                <line
                                    key={index}
                                    x1={conn.x1}
                                    y1={conn.y1}
                                    x2={conn.x2}
                                    y2={conn.y2}
                                    stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                    strokeWidth="3"
                                />
                            ))}
                        </svg>
                        <div className="mb-5 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                            <div className="left-column">
                                {questions.map((item) => (
                                    <div
                                        key={item.left_id}
                                        className={`image-box ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                        onClick={(e) => handleLeftClick(e, item.left_id)}
                                        style={{ cursor: "pointer", padding: "10px" }}
                                    >
                                        {item.leftImage}
                                    </div>
                                ))}
                            </div>
                            <div className="right-column">
                                {questions.map((item) => (
                                    <div key={item.left_id} className="image-box moneyTag" onClick={(e) => handleRightClick(e, item.right_id)}>
                                        <div className="" style={{ cursor: "pointer", padding: "10px", background: item.rightImage, height: "50px", width: "50px" }}></div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="btn hero-btn" onClick={nextQuestion}>Next Question</div>
                    </div>
                </>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} />
                </div>
            )}
        </>
    );
};

export default MatchColor;
